<script setup lang="ts">
import {computed, nextTick, onMounted, onUnmounted, PropType, ref, watch} from "vue";
import simplebar from 'simplebar-vue';
import 'simplebar-vue/dist/simplebar.min.css';
import ProLabel from "@/components/UI/Label/ProLabel.vue";

type Option = {
  value: string,
  text: string,
  disabled?: boolean
}
const props = defineProps({
  placeholder: String,
  active: Boolean,
  position: {
    type: String as PropType<'top' | 'bottom'>,
    default: 'bottom'
  },
  scrollPosition: {
    type: String as PropType<'nearest' | 'center'>,
    default: 'center'
  },
  options: {
    type: Array as PropType<Option[]>,
    required: true
  }
})
const [model] = defineModel()
const isOpen = ref(false)
const selectRef = ref<HTMLElement>()
const selectedOption = computed(() => props.options!.find(option => option.value === model.value))
const selectOption = (option: Option) => {
  if (option.disabled) return
  model.value = option.value
  isOpen.value = false
}

const handleClickOutside = (event) => {
  if (!selectRef.value!.contains(event.target)) {
    isOpen.value = false
  }
}
watch(() => isOpen.value, async (opened) => {
  if (opened) {
    await nextTick()
    selectRef.value!.querySelector('.select__option.selected')?.scrollIntoView({block: props.scrollPosition})
  }
})
const isSelected = (value: string) => {
  return value === model.value
}
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="select" @click="isOpen = !isOpen" ref="selectRef" :class="{active}">
    <div class="select__head">
      {{ selectedOption?.text || placeholder }}
    </div>
    <simplebar data-simplebar-auto-hide="false" :class="[position]" class="select__options" v-show="isOpen">
      <div class="select__option disabled selected" v-if="placeholder" data-value="">{{ placeholder }}</div>
      <div class="select__option"
           :class="{active: !(option.disabled), selected : isSelected(option.value)}"
           v-for="option in options"
           :key="option.value"
           :data-value="option.value"
           @click.stop="selectOption(option)">
        {{ option.text }}
        <ProLabel v-if="option.disabled" class="label"/>
      </div>
    </simplebar>
  </div>
</template>
<style scoped lang="scss">
.label {
  position: absolute;
  right: 20px;
}

.select {
  position: relative;
  width: 100%;
  height: 40px;
  border-radius: 20px;
  padding: 0 20px;
  border: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  position: relative;
  outline: none;
  font-size: 12px;
  font-weight: 500;
  line-height: 14.51px;
  background: url('~@/assets/icons/arrow-down.svg') no-repeat right 20px center;
  background-color: #EFEFEF;
  color: #9A9B9C;

  &.active {
    color: #6C7BB2;
    background-color: #E9EBF3;
    font-weight: 700;
  }

  &__head {
    position: absolute;
    top: 50%;
    left: 20px;
    transform: translateY(-50%);
    color: #9A9B9C;
  }

  &__options {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 10px;
    width: 100%;
    background-color: #FFFFFF;
    border-radius: 10px;
    overflow: hidden;
    text-align: center;
    z-index: 1000;
    max-height: 250px;
    overflow-y: auto;
    box-shadow: 0px 0px 30px 0px #23252926;
    &.top{
      top: auto;
      margin-bottom: 10px;
      bottom: 100%;
    }
  }

  &__option {
    height: 40px;
    display: flex;
    align-items: center;
    text-align: center;
    justify-content: center;
    color: #363739;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    position: relative;

    &.disabled {
      color: #E9EBF3;
    }

    &.selected {
      color: #6C7BB2;
      font-weight: 700;
    }

    &.active:hover {
      color: #6C7BB2;
      background-color: #E9EBF3;
    }
  }

}
</style>
